import {
  Track,
  RaceStartMsg,
  Snapshot,
  SnapshotShip,
  InputFrame,
  emptyInput,
  copyInput,
  ShipState,
  stepShip,
  getVehicle,
  StepContext,
  Vec3,
  Quat,
  unpackQuat,
  encodeInputPacket,
  TICK_DT,
  TICK_RATE,
  SHIP_FLAG_GROUNDED,
  SHIP_FLAG_BOOSTING,
  SHIP_FLAG_DRIFTING,
  SHIP_FLAG_FINISHED,
  SHIP_FLAG_ELIMINATED,
  SHIP_FLAG_RESPAWNING,
  SHIP_FLAG_INJUMP,
  SHIP_FLAG_WRONGWAY,
} from '@voidline/shared';
import { Connection } from '../net/connection.js';
import { GameDriver, RaceView, makeShipView, GameEvents, LocalRaceState } from './view.js';
import { InputManager } from '../input.js';
import { Settings } from '../persist.js';

const INTERP_DELAY_TICKS = 7; // ~117ms behind server for remote ships
const SNAP_BUFFER_MAX = 40;
const MAX_PREDICTION_REPLAY = 90;

interface BufferedSnapshot {
  tick: number;
  arrivedAt: number;
  ships: SnapshotShip[];
  standings: number[];
}

/**
 * Online play: the server simulates; we predict our own ship from local inputs,
 * reconcile against server snapshots, and interpolate everyone else.
 */
export class NetGame implements GameDriver {
  readonly view: RaceView;
  private track: Track;
  private myIndex: number;

  // prediction
  private predicted: ShipState | null = null;
  private ctx: StepContext;
  private inputHistory = new Map<number, InputFrame>();
  private currentSeq = 0;
  private accumulator = 0;
  private racingStarted = false;
  private sendCounter = 0;
  private displayError = new Vec3(); // smoothed correction offset
  private lastReconcileError = 0;

  // server clock estimation
  private snapshots: BufferedSnapshot[] = [];
  private latestSnapTick = -9999;
  private latestSnapAt = 0;
  private serverClock = -9999; // smoothed server tick estimate (float)
  private packetTimes: number[] = [];

  // race meta from snapshots
  private myLapStartTick = 0;
  private myLastLap = 1;
  private myBestLap = 0;
  private myLastLapTicks = 0;
  private lastBeepSecond = 4;
  private wentGo = false;
  private eliminatedCount = 0;

  private scratchQuat = new Quat();
  private scratchInput = emptyInput();
  private over = false;

  constructor(
    private start: RaceStartMsg,
    trackResolved: Track,
    private conn: Connection,
    private inputMgr: InputManager,
    _settings: Settings,
    private events?: GameEvents
  ) {
    this.track = trackResolved;
    this.myIndex = start.yourShipIndex;

    this.view = {
      track: this.track,
      mode: start.mode,
      laps: start.laps,
      phase: 'countdown',
      countdownSeconds: start.countdownTicks / TICK_RATE,
      raceTicks: Math.min(0, start.currentTick),
      ships: start.ships.map(makeShipView),
      standings: start.ships.map((s) => s.shipIndex),
      localIndex: this.myIndex,
      local: null,
      ghost: null,
      net: { ping: 0, packetsPerSec: 0, reconcileError: 0, interpDelayMs: (INTERP_DELAY_TICKS * 1000) / TICK_RATE },
      eliminationIn: null,
    };

    this.ctx = {
      track: this.track,
      dt: TICK_DT,
      events: {
        onWallHit: (_s, i) => this.events?.onWallHit?.(i, true),
        onBoostPad: () => this.events?.onBoostPad?.(true),
        onLand: (_s, i) => this.events?.onLand?.(i, true),
        onRespawnStart: () => this.events?.onRespawn?.(true),
        onFallOff: () => this.events?.onFallOff?.(true),
      },
    };

    if (this.myIndex >= 0) {
      const info = start.ships[this.myIndex];
      const ship = new ShipState(this.myIndex, getVehicle(info.vehicleId).stats);
      // spawn pose — will be corrected by the first snapshot anyway
      const pose = { pos: new Vec3(), frame: { pos: new Vec3(), tangent: new Vec3(0, 0, 1), up: new Vec3(0, 1, 0), right: new Vec3(1, 0, 0), width: 24 } };
      this.track.spawnPose(this.myIndex, pose);
      ship.pos.copy(pose.pos);
      ship.forward.copy(pose.frame.tangent);
      ship.up.copy(pose.frame.up);
      this.predicted = ship;
    }

    this.serverClock = start.currentTick;
    this.conn.onSnapshot = (snap) => this.onSnapshot(snap);
  }

  private onSnapshot(snap: Snapshot): void {
    const now = performance.now();
    this.packetTimes.push(now);
    if (this.packetTimes.length > 40) this.packetTimes.shift();

    // signed tick: countdown snapshots come as negative values encoded in u32
    let tick = snap.tick;
    if (tick > 0x7fffffff) tick -= 0x100000000;

    this.snapshots.push({ tick, arrivedAt: now, ships: snap.ships, standings: snap.standings });
    if (this.snapshots.length > SNAP_BUFFER_MAX) this.snapshots.shift();
    if (tick > this.latestSnapTick) {
      this.latestSnapTick = tick;
      this.latestSnapAt = now;
    }

    // eliminations for the elimination-timer estimate
    let elim = 0;
    for (const s of snap.ships) if (s.flags & SHIP_FLAG_ELIMINATED) elim++;
    this.eliminatedCount = elim;

    // ── reconciliation for our own ship ──
    if (this.predicted && this.racingStarted) {
      const mine = snap.ships.find((s) => s.index === this.myIndex);
      if (mine) {
        this.reconcile(mine, snap.lastInputSeq);
      }
    }
  }

  private reconcile(server: SnapshotShip, lastInputSeq: number): void {
    const ship = this.predicted!;
    const beforeX = ship.pos.x, beforeY = ship.pos.y, beforeZ = ship.pos.z;

    // adopt authoritative state
    ship.pos.copy(server.pos);
    ship.vel.copy(server.vel);
    unpackQuat(server.quatPacked, this.scratchQuat);
    basisFromQuat(this.scratchQuat, ship.up, ship.forward);
    ship.boostEnergy = server.boostEnergy;
    ship.respawnTimer = server.flags & SHIP_FLAG_RESPAWNING ? Math.max(ship.respawnTimer, 0.4) : 0;
    // re-anchor spline binding near the server pos
    const proj = { splineId: ship.splineId, s: ship.s, lateral: 0, height: 0, distSq: 0, progress: 0, width: 24 };
    this.track.project(ship.pos, { splineId: ship.splineId, s: ship.s }, proj);
    ship.splineId = proj.splineId;
    ship.s = proj.s;

    // replay unacknowledged inputs
    let replayed = 0;
    for (let seq = lastInputSeq + 1; seq <= this.currentSeq && replayed < MAX_PREDICTION_REPLAY; seq++) {
      const frame = this.inputHistory.get(seq);
      if (!frame) continue;
      stepShip(ship, frame, this.noEventCtx());
      replayed++;
    }

    // measure correction and fold it into a smoothed display offset
    const errX = beforeX - ship.pos.x;
    const errY = beforeY - ship.pos.y;
    const errZ = beforeZ - ship.pos.z;
    const errMag = Math.sqrt(errX * errX + errY * errY + errZ * errZ);
    this.lastReconcileError = errMag;
    if (errMag < 6) {
      this.displayError.x += errX;
      this.displayError.y += errY;
      this.displayError.z += errZ;
    } else {
      this.displayError.set(0, 0, 0); // big desync: snap
    }
    // drop acknowledged input history
    for (const seq of this.inputHistory.keys()) {
      if (seq <= lastInputSeq) this.inputHistory.delete(seq);
    }
  }

  private quietCtx: StepContext | null = null;
  private noEventCtx(): StepContext {
    if (!this.quietCtx) this.quietCtx = { track: this.track, dt: TICK_DT };
    return this.quietCtx;
  }

  update(dt: number): void {
    const now = performance.now();

    // smoothed server clock: estimate then damp toward it
    if (this.latestSnapTick > -9000) {
      const estimate = this.latestSnapTick + ((now - this.latestSnapAt) / 1000) * TICK_RATE;
      if (Math.abs(estimate - this.serverClock) > 30) this.serverClock = estimate;
      else this.serverClock += (estimate - this.serverClock) * Math.min(1, dt * 4);
      this.serverClock += 0; // (advanced by estimate blend above)
    }
    // green light detection
    if (!this.racingStarted && this.serverClock >= 0 && this.latestSnapTick >= 0) {
      this.racingStarted = true;
      this.wentGo = true;
      this.events?.onGo?.();
    }
    // countdown beeps
    if (!this.racingStarted && this.serverClock < 0) {
      const secs = Math.ceil(-this.serverClock / TICK_RATE);
      if (secs < this.lastBeepSecond && secs > 0) {
        this.lastBeepSecond = secs;
        this.events?.onCountdownBeep?.(secs);
      }
    }

    // ── prediction: fixed-rate local ticks ──
    if (this.predicted && this.racingStarted && !this.over) {
      this.accumulator += Math.min(dt, 0.25);
      while (this.accumulator >= TICK_DT) {
        this.accumulator -= TICK_DT;
        this.currentSeq++;
        const frame = emptyInput();
        this.inputMgr.sample(TICK_DT, frame);
        this.inputHistory.set(this.currentSeq, frame);
        if (this.inputHistory.size > 240) {
          const oldest = this.currentSeq - 240;
          this.inputHistory.delete(oldest);
        }
        stepShip(this.predicted, frame, this.ctx);

        // send inputs every other tick (30Hz), 6 redundant frames
        this.sendCounter++;
        if (this.sendCounter % 2 === 0) {
          const frames: InputFrame[] = [];
          for (let seq = Math.max(1, this.currentSeq - 5); seq <= this.currentSeq; seq++) {
            const f = this.inputHistory.get(seq);
            if (f) frames.push(f);
          }
          if (frames.length) this.conn.sendBinary(encodeInputPacket({ seq: this.currentSeq, frames }));
        }
      }
      // decay display error
      const decay = Math.exp(-8 * dt);
      this.displayError.scale(decay);
    }

    this.buildView(now);
  }

  private buildView(now: number): void {
    const v = this.view;
    v.phase = this.over ? 'finished' : this.racingStarted ? 'racing' : 'countdown';
    v.countdownSeconds = Math.max(0, -this.serverClock / TICK_RATE);
    v.raceTicks = Math.max(0, this.serverClock);
    if (v.net) {
      v.net.ping = this.conn.rtt;
      v.net.reconcileError = this.lastReconcileError;
      const cutoff = now - 2000;
      v.net.packetsPerSec = (this.packetTimes.filter((t) => t > cutoff).length / 2) | 0;
    }

    // remote interpolation target time
    const renderTick = this.serverClock - INTERP_DELAY_TICKS;
    const { a, b } = this.bracket(renderTick);

    const latest = this.snapshots[this.snapshots.length - 1];
    if (latest) v.standings = latest.standings;

    for (const sv of v.ships) {
      const idx = sv.info.shipIndex;
      if (idx === this.myIndex && this.predicted && this.racingStarted) {
        // own ship: predicted pose + smoothed correction offset
        const p = this.predicted;
        sv.pos.copy(p.pos).add(this.displayError);
        p.basis(sv.quat, scratchRight);
        sv.vel.copy(p.vel);
        sv.speed = p.speed();
        sv.grounded = p.grounded;
        sv.boosting = p.boosting;
        sv.drifting = p.drifting;
        sv.respawning = p.respawnTimer > 0;
        sv.inJump = p.inJump;
        sv.boostEnergy = p.boostEnergy;
        sv.wallContact = p.wallContact;
        sv.draftFactor = p.draftFactor;
        const mine = latest?.ships.find((s) => s.index === idx);
        if (mine) {
          sv.lap = mine.lap;
          sv.nextCheckpoint = mine.nextCheckpoint;
          sv.finished = (mine.flags & SHIP_FLAG_FINISHED) !== 0;
          sv.eliminated = (mine.flags & SHIP_FLAG_ELIMINATED) !== 0;
          sv.wrongWay = (mine.flags & SHIP_FLAG_WRONGWAY) !== 0;
          this.trackMyLaps(mine);
        }
        sv.steer = this.inputHistory.get(this.currentSeq)?.steer ?? 0;
        continue;
      }

      // remote / spectated ships: interpolate between snapshots
      const sa = a?.ships.find((s) => s.index === idx);
      const sb = b?.ships.find((s) => s.index === idx);
      if (sa && sb && b!.tick > a!.tick) {
        const t = Math.max(0, Math.min(1.25, (renderTick - a!.tick) / (b!.tick - a!.tick)));
        sv.pos.copy(sa.pos).lerp(sb.pos, t);
        unpackQuat(sa.quatPacked, quatA);
        unpackQuat(sb.quatPacked, quatB);
        sv.quat.copy(quatA).slerp(quatB, Math.max(0, Math.min(1, t)));
        sv.vel.copy(sb.vel);
        this.applyFlagsToView(sv, sb);
      } else if (sa || sb) {
        const s = (sb ?? sa)!;
        const snapTick = (sb ? b!.tick : a!.tick);
        // extrapolate briefly using velocity
        const aheadTicks = Math.max(0, Math.min(8, renderTick - snapTick));
        sv.pos.copy(s.pos).addScaled(s.vel, (aheadTicks * 1) / TICK_RATE);
        unpackQuat(s.quatPacked, sv.quat);
        sv.vel.copy(s.vel);
        this.applyFlagsToView(sv, s);
      }
      sv.speed = sv.vel.length();
    }

    // local HUD state
    if (this.myIndex >= 0 && this.predicted) {
      const mine = latest?.ships.find((s) => s.index === this.myIndex);
      const lap = mine?.lap ?? 1;
      const raceTick = Math.max(0, Math.round(this.serverClock));
      const local: LocalRaceState = {
        lap: Math.min(lap, this.start.laps),
        totalLaps: this.start.laps,
        nextCheckpoint: mine?.nextCheckpoint ?? 1,
        checkpointCount: this.track.checkpoints.length,
        raceTimeTicks: raceTick,
        currentLapTicks: raceTick - this.myLapStartTick,
        bestLapTicks: this.myBestLap,
        lastLapTicks: this.myLastLapTicks,
        splitDeltaMs: null,
        wrongWay: mine ? (mine.flags & SHIP_FLAG_WRONGWAY) !== 0 : false,
        placement: latest ? latest.standings.indexOf(this.myIndex) + 1 : 0,
        finished: mine ? (mine.flags & SHIP_FLAG_FINISHED) !== 0 : false,
        eliminated: mine ? (mine.flags & SHIP_FLAG_ELIMINATED) !== 0 : false,
        finishTick: 0,
        boostEnergy: this.predicted.boostEnergy,
        speed: this.predicted.speed(),
        lapFraction: 0,
      };
      v.local = local;
    } else {
      v.local = null;
    }

    // elimination timer estimate (mirrors server arming logic)
    if (this.start.mode === 'elimination' && this.racingStarted && !this.over) {
      const armTick = TICK_RATE * 25;
      const interval = this.start.eliminationInterval * TICK_RATE;
      const nextTick = armTick + this.eliminatedCount * interval;
      v.eliminationIn = Math.max(0, (nextTick - this.serverClock) / TICK_RATE);
    } else {
      v.eliminationIn = null;
    }
  }

  private applyFlagsToView(sv: (typeof this.view.ships)[number], s: SnapshotShip): void {
    sv.grounded = (s.flags & SHIP_FLAG_GROUNDED) !== 0;
    sv.boosting = (s.flags & SHIP_FLAG_BOOSTING) !== 0;
    sv.drifting = (s.flags & SHIP_FLAG_DRIFTING) !== 0;
    sv.finished = (s.flags & SHIP_FLAG_FINISHED) !== 0;
    sv.eliminated = (s.flags & SHIP_FLAG_ELIMINATED) !== 0;
    sv.respawning = (s.flags & SHIP_FLAG_RESPAWNING) !== 0;
    sv.inJump = (s.flags & SHIP_FLAG_INJUMP) !== 0;
    sv.boostEnergy = s.boostEnergy;
    sv.wallContact = s.wallContact;
    sv.steer = s.steer;
    sv.lap = s.lap;
    sv.nextCheckpoint = s.nextCheckpoint;
    sv.visible = !sv.eliminated || sv.finished;
  }

  private trackMyLaps(mine: SnapshotShip): void {
    if (mine.lap > this.myLastLap) {
      const raceTick = Math.max(0, Math.round(this.serverClock));
      const lapTicks = raceTick - this.myLapStartTick;
      this.myLastLapTicks = lapTicks;
      if (this.myBestLap === 0 || lapTicks < this.myBestLap) this.myBestLap = lapTicks;
      this.myLapStartTick = raceTick;
      this.myLastLap = mine.lap;
      this.events?.onLapComplete?.(true, lapTicks, this.myBestLap === lapTicks);
    }
  }

  private bracket(renderTick: number): { a: BufferedSnapshot | null; b: BufferedSnapshot | null } {
    let a: BufferedSnapshot | null = null;
    let b: BufferedSnapshot | null = null;
    for (let i = this.snapshots.length - 1; i >= 0; i--) {
      const s = this.snapshots[i];
      if (s.tick <= renderTick) {
        a = s;
        b = this.snapshots[i + 1] ?? null;
        break;
      }
    }
    if (!a && this.snapshots.length) {
      a = this.snapshots[0];
      b = this.snapshots[1] ?? null;
    }
    return { a, b };
  }

  markOver(): void {
    this.over = true;
  }

  isOver(): boolean {
    return this.over;
  }

  setPaused(_p: boolean): void {
    // multiplayer never pauses the sim; the pause menu is just an overlay
  }

  dispose(): void {
    this.conn.onSnapshot = null;
  }
}

const scratchRight = new Vec3();
const quatA = new Quat();
const quatB = new Quat();

/** Extract up (+Y) and forward (+Z) basis columns from a quat. */
function basisFromQuat(q: Quat, up: Vec3, forward: Vec3): void {
  up.set(0, 1, 0).applyQuat(q);
  forward.set(0, 0, 1).applyQuat(q);
}
